import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailRetailCategoriesTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailRetailCategory = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [categoryGroup, setCategoryGroup] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [eligibleForDiscount, setEligibleForDiscount] = useState<boolean>(true);
  const [eligibleForCostPlus, setEligibleForCostPlus] =
    useState<boolean>(false);
  const [categoryGroupErr, setCategoryGroupErr] = useState<boolean>(false);
  const [categoryNameErr, setCategoryNameErr] = useState<boolean>(false);
  const [retailCategories, setRetailCategories] = useState<any>([]);
  const [categoryGroupList, setCategoryGroupList] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Category Group": categoryGroup,
    "Category Name": categoryName,
    Active: active ? "Yes" : "No",
    "Eligible for Discount": eligibleForDiscount ? "Yes" : "No",
    "Eligible for Cost Plus": eligibleForCostPlus ? "Yes" : "No",
  };

  const validateInput = () => {
    setCategoryGroupErr(false);
    setCategoryNameErr(false);
    if (
      categoryGroup &&
      !validateString(
        categoryGroup,
        validationLength.categoryGroup,
        validationType.SPACE,
      )
    ) {
      setCategoryGroupErr(true);
      return false;
    }
    if (
      categoryName &&
      !validateString(
        categoryName,
        validationLength.golfCategoryName,
        validationType.NAME,
      )
    ) {
      setCategoryNameErr(true);
      return false;
    }
    return true;
  };

  const editRetailRetailCategory = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setCategoryGroup(null);
      setCategoryName(null);
      setActive(true);
      setEligibleForDiscount(true);
      setEligibleForCostPlus(false);
      setCategoryGroupErr(false);
      setCategoryNameErr(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setCategoryGroup(list?.["Category Group"] ?? null);
    setCategoryName(list?.["Category Name"] ?? null);
    setActive(list?.["Active"] === "Yes");
    setEligibleForDiscount(list?.["Eligible for Discount"] === "Yes");
    setEligibleForCostPlus(list?.["Eligible for Cost Plus"] === "Yes");
  };

  const updateRetailRetailCategory = () => {
    if (!categoryName || String(categoryName).trim() === "") {
      setAlertMessage("Category Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailCategories.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.CATEGORYNAME,
        categoryName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setCategoryGroupErr(false);
    setCategoryNameErr(false);
    setRetailCategories((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailRetailCategory(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.retailCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailRetailCategory = () => {
    if (!categoryName || String(categoryName).trim() === "") {
      setAlertMessage("Category Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(retailCategories, DNDFieldsEnum.CATEGORYNAME, categoryName)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailCategories((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailCategories.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailRetailCategory(null);
    setEditClicked(false);
  };

  const deleteRetailRetailCategory = (id: number) => {
    setRetailCategories(deleteItem(retailCategories, id));
  };

  useEffect(() => {
    if (fieldValues) {
      setRetailCategories(fieldValues[RetailPropertyEnum.retailCategories]);
      setCategoryGroupList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.categoryGroups],
          DNDFieldsEnum.CATEGORYGROUP,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(retailCategories);
    editClicked &&
      setSelectedId(
        checkData(retailCategories, selectedId) ? selectedId : null,
      );
  }, [retailCategories]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Retail Categories
              <Tooltip
                title={tooltipComponent(retailRetailCategoriesTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailRetailCategory(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailRetailCategory(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Category Group
              <Tooltip
                title={tooltipComponent(
                  retailRetailCategoriesTooltip.categoryGroup,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                id="retailRetailCategoryGroupList"
                disablePortal
                options={removeSampleTestArray(categoryGroupList)}
                sx={{ width: "100%" }}
                value={categoryGroup}
                disabled={!categoryGroupList}
                onChange={(_e, newValue) => {
                  setCategoryGroup(newValue ?? null);
                  setCategoryGroupErr(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category Group"
                    variant="outlined"
                    placeholder="Category Group"
                    error={categoryGroupErr}
                    helperText={
                      categoryGroupErr ? validationMessages.categoryGroup : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Category Name
              <Tooltip
                title={tooltipComponent(
                  retailRetailCategoriesTooltip.categoryName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailRetailCategoryName"
              variant="outlined"
              placeholder="Category Name"
              value={categoryName ?? ""}
              helperText={
                categoryNameErr
                  ? validationMessages.retailCategoryName
                  : fieldValidationHelperText.categoryName
              }
              error={categoryNameErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCategoryName(e.target.value);
                setCategoryNameErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(retailRetailCategoriesTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Eligible for Discount?
              <Tooltip
                title={tooltipComponent(
                  retailRetailCategoriesTooltip.eligibleForDiscount,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={eligibleForDiscount}
                  onChange={() => setEligibleForDiscount(!eligibleForDiscount)}
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Eligible for Cost Plus?
              <Tooltip
                title={tooltipComponent(
                  retailRetailCategoriesTooltip.eligibleForCostPlus,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={eligibleForCostPlus}
                  onChange={() => setEligibleForCostPlus(!eligibleForCostPlus)}
                />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailRetailCategory
                  : addRetailRetailCategory
              }
              id="addRetailRetailCategory"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Retail
              Category
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Retail Categories Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailCategories?.length ? (
                <DraggableItemList
                  itemData={retailCategories}
                  onOrderUpdate={(order) => setRetailCategories(order)}
                  onEdit={(info) => {
                    editRetailRetailCategory(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailRetailCategory(id)}
                  code={DNDFieldsEnum.CATEGORYNAME}
                  name={DNDFieldsEnum.CATEGORYGROUP}
                />
              ) : (
                <Typography variant="body2">
                  No Retail Categories Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailRetailCategory;
